import React, { useState, createContext } from 'react';
import { getCookie, setCookie, GIJP_AUTH_COOKIE } from '~/index';

const EXPIRATION_DAYS_AUTH_TOKEN = 90;

/**
 * Represents the authentication data for a user.
 *
 * @typedef {Object} AuthDataProps
 * @property {string} token - The authentication token for the user.
 * @property {string} userId - The unique identifier for the user.
 * @property {string} error - An error message related to authentication.
 * @property {Array<string>} permissions - A list of permissions assigned to the user.
 */

/**
 * Default authentication data.
 *
 * This object represents the initial state of the authentication data.
 *
 * @type {AuthDataProps}
 */
const defaultAuthData = {
  token: '',
  userId: '',
  error: '',
  permissions: [''],
};

export const AuthContext = createContext({
  authData: defaultAuthData,
});

/**
 * AuthProvider Component
 *
 * This component provides the `AuthContext` to its children, allowing them to access
 * and manage authentication data. It also handles storing and retrieving authentication
 * data in cookies.
 *
 * @param {Object} props - Component properties.
 * @param {React.ReactElement} props.children - The child components that will have access to the `AuthContext`.
 * @returns {React.ReactElement} The `AuthProvider` component wrapping its children.
 */
const AuthProvider = ({ children }) => {
  const authUser = getCookie(GIJP_AUTH_COOKIE);
  const [authData, setAuthData] = useState(
    authUser ? { ...JSON.parse(authUser), error: '' } : defaultAuthData,
  );

  /**
   * Updates the authentication data and stores it in a cookie.
   *
   * @param {AuthDataProps} data - The new authentication data to be set.
   */
  const handleSetAuthData = (data) => {
    setCookie(
      GIJP_AUTH_COOKIE,
      JSON.stringify(data),
      EXPIRATION_DAYS_AUTH_TOKEN,
    );
    setAuthData(data);
  };

  return (
    <AuthContext.Provider
      value={{
        authData,
        setAuthData: handleSetAuthData,
        resetAuthData: () => setAuthData(defaultAuthData),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
